export type EventDeliveryClass = "essential" | "analytics" | "marketing";

// Keep this tiny browser-side list synchronized with
// @prospix/event-schemas. The focused contract test compares both arrays
// without bundling the server schema/Zod graph into the runtime.
export const CLIENT_ANONYMOUS_ANALYTICS_EVENT_TYPES = [
  "page_view",
  "page_exit",
  "click",
  "download",
  "engagement",
  "scroll_depth",
  "video_start",
  "video_progress",
  "video_complete",
  "web_vitals",
  "identity_opportunity_blocked_consent",
] as const;

const anonymousAnalyticsEventTypes = new Set<string>(
  CLIENT_ANONYMOUS_ANALYTICS_EVENT_TYPES,
);

const ESSENTIAL_EVENT_TYPES = new Set([
  "consent_update",
  "diagnostic",
  "pixel_error",
  "traffic_presence",
  "bot_signal",
]);

const MARKETING_EVENT_TYPES = new Set([
  "custom.booking_requested",
  "custom.booking_confirmed",
  "custom.booking_cancelled",
  "custom.demo_scheduled",
  "add_payment_info",
  "add_to_cart",
  "attribution_update",
  "begin_checkout",
  "checkout_step",
  "checkout_started",
  "contact_sales",
  "content_view",
  "commerce_abandonment",
  "coupon_apply",
  "coupon_remove",
  "decision_candidate",
  "decision_exposure",
  "decision_outcome",
  "decision_recorded",
  "demo_requested",
  "demo_scheduled",
  "identify",
  "identity_linked",
  "identity_resolution",
  "intent_update",
  "lead",
  "login",
  "outbound_click",
  "payment_method_selected",
  "purchase",
  "remove_from_cart",
  "search",
  "shipping_method_selected",
  "shopify_cart_update",
  "shopify_checkout_step",
  "shopify_purchase",
  "signup",
  "subscription_cancel",
  "subscription_start",
  "trial_start",
  "view_item",
  "cta_click",
]);

const ANONYMOUS_ANALYTICS_PLUGIN_NAMES = new Set([
  "click",
  "download",
  "engagement",
  "router",
  "scroll",
  "video",
  "webvitals",
]);

export function isEssentialEvent(type: string) {
  return ESSENTIAL_EVENT_TYPES.has(type);
}

export function resolveEventDeliveryClass(
  type: string,
  requiredClass?: EventDeliveryClass,
): EventDeliveryClass {
  // A marketing source must never gain an essential exemption by choosing an
  // essential-looking event name. Only an explicitly trusted essential source
  // or the runtime's fixed event allowlist may use essential delivery.
  if (requiredClass === "marketing" || MARKETING_EVENT_TYPES.has(type)) {
    return "marketing";
  }
  if (requiredClass) return requiredClass;
  return isEssentialEvent(type) ? "essential" : "analytics";
}

export function shouldUseAnonymousAnalyticsFallback(
  deliveryClass: EventDeliveryClass,
  identifierCreationAllowed: boolean,
) {
  return deliveryClass === "analytics" && !identifierCreationAllowed;
}

export function resolveAnonymousAnalyticsFallbackPolicy(
  deliveryClass: EventDeliveryClass,
  identifierCreationAllowed: boolean,
) {
  const active = shouldUseAnonymousAnalyticsFallback(
    deliveryClass,
    identifierCreationAllowed,
  );
  return {
    active,
    attachVisitor: !active,
    attachContext: !active,
    attachProperties: !active,
  };
}

export function isAnonymousAnalyticsFallbackEvent(type: string) {
  return anonymousAnalyticsEventTypes.has(type);
}

export function isAnonymousAnalyticsFallbackPlugin(name: string) {
  return ANONYMOUS_ANALYTICS_PLUGIN_NAMES.has(name);
}
